import { HQ_BASE } from '../constants';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hqStoreService, hqBillingService, HqStore, HqBillingInfo, HqAlimtalkCredit } from '../services/hqStoreService';
import { hqAuthService } from '../services/hqAuthService';

const HqStoreDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isAdmin = hqAuthService.getCachedUser()?.role === 'hq_admin';

  const [store, setStore] = useState<HqStore | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<HqStore>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'info' | 'billing'>('info');
  const [credit, setCredit] = useState<HqAlimtalkCredit | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const s = await hqStoreService.get(id);
      setStore(s);
      setForm(s);
      // 카톡 크레딧 잔액 (실패해도 매장 상세는 표시)
      try {
        setCredit(await hqStoreService.alimtalkCredit(id));
      } catch {
        setCredit(null);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || '가맹점 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      const updated: any = {};
      const editable = ['name', 'region', 'seat_count', 'area_pyeong', 'voucher_price',
        'owner_name', 'owner_phone', 'owner_email', 'contract_started_at', 'contract_ended_at',
        'franchise_fee', 'franchise_fee_paid_at', 'franchise_status', 'notes'];
      for (const k of editable) {
        if ((form as any)[k] !== (store as any)[k]) updated[k] = (form as any)[k];
      }
      if (Object.keys(updated).length === 0) {
        setEditing(false);
        return;
      }
      await hqStoreService.update(id, updated);
      setEditing(false);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '저장 실패');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-slate-400">불러오는 중...</div>;
  if (error || !store) {
    return (
      <div>
        <button onClick={() => navigate(`${HQ_BASE}/stores`)} className="text-blue-600 mb-4">← 목록으로</button>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
          {error || '가맹점을 찾을 수 없습니다.'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(`${HQ_BASE}/stores`)} className="text-slate-500 hover:text-slate-700">
            ← 목록
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">🏪 {store.name}</h1>
          <StatusBadge color={store.status_color} />
        </div>
        {isAdmin && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg"
          >
            수정
          </button>
        )}
      </div>

      {/* 운영 지표 카드 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Stat label="좌석 / 학생" value={`${store.student_count} / ${store.seat_count ?? '-'}`} sub={store.occupancy_rate != null ? `점유율 ${store.occupancy_rate}%` : ''} />
        <Stat
          label="유형"
          value={store.customer_type === 'sale' ? '판매형' : '구독형'}
          sub={store.customer_type === 'sale' ? '로컬 서버 / 1회 구매' : '클라우드 / 월 구독'}
        />
        <Stat
          label="구독 상태"
          value={store.customer_type === 'sale' ? '해당없음' :
            ({ active: '구독중', trial: '체험', suspended: '정지(미납)', expired: '만료' } as any)[store.subscription_status || ''] || '미설정'}
          sub={store.plan_ends_at ? `~ ${store.plan_ends_at}` : ''}
        />
      </div>

      {/* 카톡 알림톡 크레딧 잔액 */}
      {credit && (
        <div
          className={`mb-6 rounded-lg border p-4 ${
            credit.remaining_count <= 0
              ? 'bg-red-50 border-red-300'
              : credit.remaining_count < 300
                ? 'bg-yellow-50 border-yellow-300'
                : 'bg-white border-slate-200'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg">💬</span>
              <span className="font-semibold text-slate-800">카톡 알림톡 크레딧</span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full border ${
                  credit.prepaid_enabled
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-slate-100 text-slate-500 border-slate-200'
                }`}
              >
                {credit.prepaid_enabled ? '선불 (소진 시 발송중단)' : '후불'}
              </span>
            </div>
            <div className="text-right">
              <div
                className={`text-xl font-bold ${
                  credit.remaining_count <= 0
                    ? 'text-red-600'
                    : credit.remaining_count < 300
                      ? 'text-yellow-700'
                      : 'text-slate-800'
                }`}
              >
                약 {credit.remaining_count.toLocaleString()}건
              </div>
              <div className="text-xs text-slate-500">
                잔액 {credit.balance.toLocaleString()}원
                {credit.free_remaining > 0 && ` · 무료 ${credit.free_remaining}건`}
              </div>
            </div>
          </div>
          {credit.remaining_count <= 0 && (
            <div className="mt-2 text-xs font-medium text-red-600">
              ⚠️ 발송 가능 건수 소진 —{' '}
              {credit.prepaid_enabled ? '알림톡 발송이 중단됩니다.' : '충전이 필요합니다.'}
            </div>
          )}
        </div>
      )}

      {/* 탭 */}
      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {[
          { key: 'info' as const, label: '📋 기본 정보' },
          { key: 'billing' as const, label: '💳 구독·청구' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'billing' ? (
        <BillingTab store={store} isAdmin={isAdmin} />
      ) : (
      <div className="bg-white rounded-xl shadow p-6">
        {editing ? (
          <EditForm form={form} setForm={setForm} />
        ) : (
          <ViewMode store={store} />
        )}

        {editing && (
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <button
              onClick={() => { setEditing(false); setForm(store); }}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={submitting}
              className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg disabled:bg-slate-400"
            >
              {submitting ? '저장 중...' : '저장'}
            </button>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ color: string }> = ({ color }) => {
  const map: Record<string, { label: string; cls: string }> = {
    green: { label: '안전', cls: 'bg-green-100 text-green-700' },
    yellow: { label: '주의', cls: 'bg-yellow-100 text-yellow-700' },
    red: { label: '위험', cls: 'bg-red-100 text-red-700' },
  };
  const m = map[color] || map.yellow;
  return <span className={`px-2 py-0.5 text-xs font-semibold rounded ${m.cls}`}>{m.label}</span>;
};

const Stat: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl shadow p-5">
    <div className="text-sm text-slate-500">{label}</div>
    <div className="text-xl sm:text-2xl font-bold text-slate-800 mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

const ViewMode: React.FC<{ store: HqStore }> = ({ store }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
    <Row label="지역" value={store.region || '-'} />
    <Row label="유형" value={store.franchise_status === 'franchisee' ? '가맹점' : store.franchise_status === 'direct' ? '직영점' : '단독'} />
    <Row label="평수" value={store.area_pyeong ? `${store.area_pyeong}평` : '-'} />
    <Row label="계약일" value={store.contract_started_at || '-'} />
    <Row label="가맹점주" value={store.owner_name || '-'} />
    <Row label="점주 전화" value={store.owner_phone || '-'} />
    <Row label="점주 이메일" value={store.owner_email || '-'} />
    <Row label="계약 종료일" value={store.contract_ended_at || '진행중'} />
    <Row label="메모" value={store.notes || '-'} fullWidth />
  </dl>
);

const Row: React.FC<{ label: string; value: string; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div className={fullWidth ? 'col-span-full' : ''}>
    <dt className="text-xs text-slate-500">{label}</dt>
    <dd className="text-slate-800 mt-0.5">{value}</dd>
  </div>
);

const EditForm: React.FC<{ form: any; setForm: (f: any) => void }> = ({ form, setForm }) => {
  const update = (k: string, v: any) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <EditField label="매장명" value={form.name || ''} onChange={(v) => update('name', v)} />
        <EditField label="지역" value={form.region || ''} onChange={(v) => update('region', v)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <EditField label="좌석 수" type="number" value={String(form.seat_count ?? '')} onChange={(v) => update('seat_count', Number(v) || 0)} />
        <EditField label="평수" type="number" value={String(form.area_pyeong ?? '')} onChange={(v) => update('area_pyeong', Number(v) || 0)} />
        <EditField label="이용권 (원)" type="number" value={String(form.voucher_price ?? '')} onChange={(v) => update('voucher_price', Number(v) || 0)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <EditField label="점주 이름" value={form.owner_name || ''} onChange={(v) => update('owner_name', v)} />
        <EditField label="점주 전화" value={form.owner_phone || ''} onChange={(v) => update('owner_phone', v)} />
        <EditField label="점주 이메일" type="email" value={form.owner_email || ''} onChange={(v) => update('owner_email', v)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <EditField label="계약 시작일" type="date" value={form.contract_started_at || ''} onChange={(v) => update('contract_started_at', v)} />
        <EditField label="계약 종료일" type="date" value={form.contract_ended_at || ''} onChange={(v) => update('contract_ended_at', v)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <EditField label="가맹비 (원)" type="number" value={String(form.franchise_fee ?? '')} onChange={(v) => update('franchise_fee', Number(v) || 0)} />
        <EditField label="가맹비 납부일" type="date" value={form.franchise_fee_paid_at || ''} onChange={(v) => update('franchise_fee_paid_at', v)} />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">메모</label>
        <textarea
          value={form.notes || ''}
          onChange={(e) => update('notes', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border rounded-lg"
        />
      </div>
    </div>
  );
};

interface EditFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}
const EditField: React.FC<EditFieldProps> = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
);

const PLAN_OPTIONS = ['FREE', 'STARTER', 'BASIC', 'PRO', 'PREMIUM'];

const BillingTab: React.FC<{ store: HqStore; isAdmin: boolean }> = ({ store, isAdmin }) => {
  const [info, setInfo] = useState<HqBillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState('BASIC');
  const [months, setMonths] = useState(1);
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');

  const ym = new Date().toISOString().slice(0, 7);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const d = await hqBillingService.get(store.id, ym);
      setInfo(d);
      if (d.organization.subscription_plan) setPlan(d.organization.subscription_plan);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || '청구 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.id]);

  if (store.customer_type === 'sale') {
    return (
      <div className="bg-white rounded-xl shadow p-8 text-center text-slate-500">
        <div className="text-3xl mb-2">🏷️</div>
        <div className="font-semibold text-slate-700">판매형 독서실</div>
        <div className="text-sm mt-1">프로그램 1회 판매 / 로컬 서버 운영 — 월 구독·청구 대상이 아닙니다.</div>
        <div className="text-xs mt-3 text-slate-400">계약·구매 메모는 「기본 정보」 탭의 메모에 기록하세요.</div>
      </div>
    );
  }

  if (loading) return <div className="text-slate-400">불러오는 중...</div>;
  if (err) return <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{err}</div>;

  const act = async (action: string, extra: any = {}) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      await hqBillingService.changeSubscription(store.id, { action, ...extra });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || '처리 실패');
    } finally {
      setBusy(false);
    }
  };

  const recordPay = async () => {
    if (!isAdmin) return;
    const amt = Number(payAmount);
    if (!amt || amt <= 0) {
      alert('입금 금액을 입력하세요.');
      return;
    }
    setBusy(true);
    try {
      await hqBillingService.recordPayment(store.id, ym, {
        amount: amt,
        payment_type: 'bank_transfer',
        notes: payNote || undefined,
      });
      setPayAmount('');
      setPayNote('');
      await load();
      alert('입금이 확인 처리되었습니다.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || '입금 기록 실패');
    } finally {
      setBusy(false);
    }
  };

  const o = info?.organization;
  const outstanding = info?.outstanding;

  return (
    <div className="space-y-4">
      {/* 구독 상태 */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">구독 상태</h3>
          <span className="text-sm text-slate-500">
            {o?.subscription_plan || '-'} · {o?.subscription_status || '미설정'}
            {o?.plan_ends_at ? ` · ~${o.plan_ends_at}` : ''}
          </span>
        </div>
        {o?.subscription_status === 'suspended' && (
          <div className="mb-3 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">
            ⛔ 정지됨 {o?.suspended_reason ? `(${o.suspended_reason})` : ''} — 학생/점주 로그인 차단 상태
          </div>
        )}
        {isAdmin ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <select value={plan} onChange={(e) => setPlan(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
                {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="px-3 py-2 border rounded-lg text-sm">
                {[1, 3, 6, 12].map((m) => <option key={m} value={m}>{m}개월</option>)}
              </select>
              <button disabled={busy} onClick={() => act('start', { plan, months })}
                className="px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm disabled:bg-slate-400">
                구독 시작/갱신
              </button>
              <button disabled={busy} onClick={() => act('change_plan', { plan })}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm">
                플랜만 변경
              </button>
              <button disabled={busy} onClick={() => act('extend', { months })}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm">
                +{months}개월 연장
              </button>
            </div>
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {o?.subscription_status === 'suspended' ? (
                <button disabled={busy} onClick={() => act('reactivate')}
                  className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm">
                  정지 해제 (재활성화)
                </button>
              ) : (
                <button disabled={busy}
                  onClick={() => { const r = window.prompt('정지 사유 (미납 등)', '미납'); if (r !== null) act('suspend', { reason: r }); }}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm">
                  미납 정지
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-400">변경은 본부 관리자만 가능합니다.</div>
        )}
      </div>

      {/* 이번달 청구/입금 */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold text-slate-800 mb-4">이번달 청구 ({ym})</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <Stat label="청구 금액" value={fmt(info?.payment_summary?.total_billed ?? info?.payment_summary?.bill_amount)} />
          <Stat label="입금 완료" value={fmt(info?.payment_summary?.total_paid ?? info?.payment_summary?.paid_amount)} />
          <Stat label="미수금(누적)" value={fmt(outstanding?.outstanding ?? outstanding?.balance)} />
        </div>

        {isAdmin && (
          <div className="flex flex-wrap items-end gap-2 pt-4 border-t">
            <div>
              <label className="block text-xs text-slate-500 mb-1">입금 금액 (수기 확인)</label>
              <input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
                placeholder="예: 50000" className="px-3 py-2 border rounded-lg text-sm w-36" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs text-slate-500 mb-1">메모 (선택)</label>
              <input type="text" value={payNote} onChange={(e) => setPayNote(e.target.value)}
                placeholder="입금자명 등" className="px-3 py-2 border rounded-lg text-sm w-full" />
            </div>
            <button disabled={busy} onClick={recordPay}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:bg-slate-400">
              입금 확인
            </button>
          </div>
        )}

        {info?.payments && info.payments.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-xs text-slate-500 mb-2">입금 내역</div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-sm">
              <tbody>
                {info.payments.map((p: any, i: number) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2">{(p.paid_at || '').slice(0, 10)}</td>
                    <td className="py-2">{p.payment_type === 'bank_transfer' ? '계좌이체' : p.payment_type === 'card' ? '카드' : p.payment_type}</td>
                    <td className="py-2 text-right font-medium">{fmt(p.amount)}</td>
                    <td className="py-2 text-slate-500">{p.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* 결제선생 자동결제 (API key 대기) */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-semibold text-slate-800 mb-2">카드 자동결제 (결제선생)</h3>
        <div className="px-3 py-3 bg-amber-50 text-amber-800 text-sm rounded">
          ⏳ 결제선생 API 키 발급 대기 중 — 키 등록 후 이 영역에서 카드 등록·자동 정기결제가 활성화됩니다.
          현재는 위의 <b>수기 입금 확인</b> 방식으로 운영하세요.
        </div>
      </div>
    </div>
  );
};

function fmt(v: any): string {
  const n = Number(v);
  if (!v || isNaN(n)) return '0원';
  return `${n.toLocaleString()}원`;
}

export default HqStoreDetailPage;
